import type { MetricsRow } from 'app/services/metrics'
import { ColumnDef } from '@tanstack/react-table'
import { LazyStats } from 'app/components/LazyStats'
import MetricCell from 'app/components/table/MetricCell'
import TagsCell from 'app/components/tags/TagsCell'
import { addDefaultFilterProperties, FilterValueType, FilterableColumn } from 'app/filter.utils'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { ActivityJson } from 'app/types/entities'
import { getActivityTypeColorMap } from 'lib/colorMappings'

export const defaultColumns = (
  showSensitiveColumns: boolean,
  isSMC: boolean,
  fromDate?: string,
  toDate?: string,
  channel?: string,
): ColumnDef<MetricsRow, any>[] => [
  {
    accessorFn: (row) => row.workspace?.name || row.bucket,
    header: `Workspace Name`,
    footer: `Total`,
    id: `Workspace Name`,
    minSize: 200,
    meta: {
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },

  {
    accessorFn: (row) => row.budget,
    id: `Budget`,
    header: `Budget`,
    cell: (props) => <MetricCell value={props.row.original.budget} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)}
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.workspace?.orgTags,
    id: `Org Tags`,
    header: `Org Tags`,
    meta: {
      ControlledComponent: (row) => (
        <TagsCell tags={row?.workspace?.orgTags} model="workspaces" modelId={row?.workspaceId!} isOrgTags />
      ),
      defaultVisible: false,
    },
    enableSorting: false,
  },
  ...(showSensitiveColumns
    ? ([
        {
          id: `iCPQL`,
          header: `iCPQL`,
          accessorFn: (row) => ((row.facebookSpend || 0) + (row.googleSpend || 0)) / row.MQLs,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={
                table
                  .getFilteredRowModel()
                  .rows.reduce((total, row) => total + row.original.facebookSpend! + row.original.googleSpend!, 0) /
                table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)
              }
              type="dollars"
            />
          ),

          meta: {},
        },
        {
          accessorFn: (row) => row.facebookSpend,
          id: `Facebook Spend`,
          header: `Facebook Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + (row.original.facebookSpend || 0), 0)}
              type="dollars"
            />
          ),
        },
        {
          accessorFn: (row) => row.googleSpend,
          id: `Google Spend`,
          header: `Google Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + (row.original.googleSpend || 0), 0)}
              type="dollars"
            />
          ),
        },
        {
          accessorFn: (row) => (row.facebookSpend || 0) + (row.googleSpend || 0),
          id: `Total Spend`,
          header: `Total Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce(
                  (total, row) => total + (row.original.facebookSpend || 0) + (row.original.googleSpend || 0),
                  0,
                )}
              type="dollars"
            />
          ),
        },
        {
          accessorFn: (row) => row.targetSpend,
          id: `Target Spend`,
          header: `Target Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + (row.original.targetSpend || 0), 0)}
              type="dollars"
            />
          ),
          meta: {
            defaultVisible: false,
          },
        },
      ] as ColumnDef<MetricsRow, any>[])
    : []),
  {
    accessorFn: (row) => row.leadGoal,
    id: `Lead Goal`,
    header: `Lead Goal`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadGoal, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leads,
    id: `Leads Generated`,
    header: `Leads Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPL,
    id: `CPL`,
    header: `CPL`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)
        }
        type="dollars"
      />
    ),

    meta: {},
  },
  {
    accessorFn: (row) => row.MQLs,
    id: `Qualified Leads Generated`,
    header: `Qualified Leads Generated`,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)}
        type="unitless"
      />
    ),
    meta: {
      ControlledComponent: (row) => (
        <LazyStats
          title="Qualified Leads Generated"
          count={row!.MQLs}
          requestPath={`/api/workspaces/${
            row!.workspaceId
          }/qualified-lead-counts?fromDate=${fromDate}&toDate=${toDate}&channel=${channel}`}
          createBreakdownFromResponse={(body: Record<string, string>) =>
            Object.entries(body).map(([key, val]) => ({
              name: key,
              count: Number(val),
              colorHex: getActivityTypeColorMap(key as ActivityJson[`type`]),
            }))
          }
        >
          <MetricCell value={row!.MQLs} type="unitless" />
        </LazyStats>
      ),
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPQL,
    id: `CPQL`,
    header: `CPQL`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)
        }
        type="dollars"
      />
    ),

    meta: {},
  },
  {
    accessorFn: (row) => row.appointments,
    id: `Appointments Generated`,
    header: `Appointments Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.appointments, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPA,
    id: `CPA`,
    header: `CPA`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.appointments, 0)
        }
        type="dollars"
      />
    ),

    meta: {},
  },
  {
    accessorFn: (row) => row.patients,
    id: `New Patients Generated`,
    header: `New Patients Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patients, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CAC,
    id: `CAC`,
    header: `CAC`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patients, 0)
        }
        type="dollars"
      />
    ),

    meta: {},
  },
  {
    accessorFn: (row) => row.production,
    id: `Total Production Generated`,
    header: `Total Production Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.production, 0)}
        type="dollars"
      />
    ),

    meta: {},
  },
  {
    accessorFn: (row) => row.ROI,
    id: `ROI`,
    header: `ROI`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          ((table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.production, 0) +
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.scheduledProduction, 0) -
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) =>
                  total +
                  (isSMC ? row.original.budget : (row.original.facebookSpend || 0) + (row.original.googleSpend || 0)),
                0,
              )) /
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) =>
                  total +
                  (isSMC ? row.original.budget : (row.original.facebookSpend || 0) + (row.original.googleSpend || 0)),
                0,
              )) *
          100
        }
        type="percentage"
      />
    ),
    meta: {},
  },
  {
    accessorFn: (row) => row.scheduleRate,
    id: `Schedule Rate`,
    header: `Schedule Rate`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    footer: ({ table }) => (
      <MetricCell
        value={Math.floor(
          (table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.appointments ?? 0), 0) /
            table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.qualifiedLeads ?? 0), 0)) *
            100,
        )}
        type="percentage"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.callScheduleRate,
    id: `Phone Schedule Rate`,
    header: `Phone Schedule Rate`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    footer: ({ table }) => (
      <MetricCell
        value={Math.floor(
          (table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.scheduledLeadsWithCalls ?? 0), 0) /
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + (row.original?.activeLeadsWithCalls ?? 0), 0)) *
            100,
        )}
        type="percentage"
      />
    ),
    meta: {},
  },
  // {
  //   accessorFn: (row) => row.leadsWithMissedCall,
  //   id: `Missed Calls`,
  //   header: `Missed Calls`,
  //   cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
  //   footer: ({ table }) => (
  //     <MetricCell
  //       value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithMissedCall, 0)}
  //       type="unitless"
  //     />
  //   ),

  //   meta: {
  //     defaultVisible: true,
  //   },
  // },
  {
    accessorFn: (row) => row.leadsDisqualified,
    id: `Disqualified`,
    header: `Disqualified`,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsDisqualified, 0)}
        type="unitless"
      />
    ),
    meta: {
      ControlledComponent: (row) => (
        <LazyStats
          title="Disqualified"
          count={row!.leadsDisqualified}
          requestPath={`/api/workspaces/${
            row!.workspaceId
          }/disqualified-reason?fromDate=${fromDate}&toDate=${toDate}&channel=${channel}`}
          createBreakdownFromResponse={(body: Record<string, string>) =>
            Object.entries(body).map(([key, val]) => ({
              name: key,
              count: Number(val),
            }))
          }
        >
          <MetricCell value={row!.leadsDisqualified} type="unitless" />
        </LazyStats>
      ),
    },
  },
  {
    accessorFn: (row) => row.callsInQueue,
    id: `Calls In Queue`,
    header: `Calls In Queue`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.callsInQueue, 0)}
        type="unitless"
      />
    ),

    meta: {},
  },
]

export const filterableColumns = (
  showSensitiveColumns: boolean,
  preAppliedFilters: { [key: string]: any },
): FilterableColumn[] => {
  const filters = [
    {
      attribute: `date`,
      displayName: `Date`,
      type: FilterValueType.rangeDate,
      customSearchParam: `date`,
      limitToMonth: true,
      includeWeeks: false,
    },
    {
      attribute: `workspace.id`,
      displayName: `Workspaces`,
      type: FilterValueType.id,
      fetcher: (params) => gqlFetchList('workspaces', { search: params.query }, 'id name'),
    },
    {
      attribute: `budget`,
      displayName: `Budget`,
      type: FilterValueType.number,
    },
    {
      attribute: `workspace.orgTags`,
      displayName: `Org Tags`,
      type: FilterValueType.stringArray,
      optionsSource: `/api/org-tags/workspaces`,
    },
    ...(showSensitiveColumns
      ? ([
          {
            attribute: `facebookSpend`,
            displayName: `Facebook Spend`,
            type: FilterValueType.number,
          },
          {
            attribute: `googleSpend`,
            displayName: `Google Spend`,
            type: FilterValueType.number,
          },
          {
            attribute: `targetSpend`,
            displayName: `Target Spend`,
            type: FilterValueType.number,
          },
        ] as FilterableColumn[])
      : []),
    {
      attribute: `workspaceId`,
      secondaryAttribute: `assignedTo`,
      displayName: `Assigned To`,
      uniqOperator: `in`,
      type: FilterValueType.assignedTo,
    },
    {
      attribute: `leadGoal`,
      displayName: `Lead Goal`,
      type: FilterValueType.number,
    },
    {
      attribute: `leads`,
      displayName: `Leads`,
      type: FilterValueType.number,
    },
    {
      attribute: `CPL`,
      displayName: `CPL`,
      type: FilterValueType.number,
    },
    {
      attribute: `MQLs`,
      displayName: `Qualified Leads`,
      type: FilterValueType.number,
    },
    {
      attribute: `CPQL`,
      displayName: `CPQL`,
      type: FilterValueType.number,
    },
    {
      attribute: `appointments`,
      displayName: `Appointments`,
      type: FilterValueType.number,
    },
    {
      attribute: `CPA`,
      displayName: `CPA`,
      type: FilterValueType.number,
    },
    {
      attribute: `patients`,
      displayName: `Patients`,
      type: FilterValueType.number,
    },
    {
      attribute: `CAC`,
      displayName: `CAC`,
      type: FilterValueType.number,
    },
    {
      attribute: `production`,
      displayName: `Production`,
      type: FilterValueType.number,
    },
    {
      attribute: `ROI`,
      displayName: `ROI`,
      type: FilterValueType.number,
    },
    {
      attribute: `conversionRate`,
      displayName: `Conversion Rate`,
      type: FilterValueType.number,
    },
    {
      attribute: `leadsWithMissedCall`,
      displayName: `Calls Missed`,
      type: FilterValueType.number,
    },
    {
      attribute: `callsInsuranceIssues`,
      displayName: `Calls Insurance Issues`,
      type: FilterValueType.number,
    },
    {
      attribute: `callsInQueue`,
      displayName: `Calls In Queue`,
      type: FilterValueType.number,
    },
  ] as FilterableColumn[]
  return filters?.map((filter) => ({ ...filter, ...addDefaultFilterProperties(filter.attribute, preAppliedFilters) }))
}
